/* Grading light is decorative. Grade positions and rail fills never animate. */
(() => {
  if (window.beyloraGradeRefinement) return;
  window.beyloraGradeRefinement = true;
  const storageKey = 'beylora:grade-glow-paused';
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  let paused = false;
  try { paused = localStorage.getItem(storageKey) === 'true'; } catch (_) { /* Static storage fallback. */ }
  const modules = new Map();
  const visible = new Set();
  const labels = new Set();
  let frame = 0;

  // CMS education uses the same readable rail and ordered-key fallback.
  function adaptGuideGradeScales() {
    document.querySelectorAll('rte-formatter:has(.bey-guide-art) .bey-quality-guide .bey-quality-scale').forEach(list => {
      const items = [...list.children];
      const highest = items.filter(item => item.classList.contains('is-highest'));
      if (!items.length || items.some(item => item.tagName !== 'LI') || highest.length !== 1) return;
      const kind = list.dataset.gradeScale || list.closest('[data-finish-grade]')?.dataset.finishGrade;
      if (!kind) return;
      const grade = document.createElement('div');
      grade.className = 'bey-grade bey-grade--education';
      grade.dataset.gradeScale = kind;
      grade.setAttribute('data-grade-key', '');
      grade.setAttribute('data-guide-grade-adapted', '');
      grade.style.setProperty('--grade-count', items.length);
      const description = document.createElement('p');
      description.className = 'visually-hidden';
      description.textContent = `${kind[0].toUpperCase() + kind.slice(1)} grades, lower on the left and higher on the right. Educational scale. The highest available grade is ${highest[0].textContent.trim()}. This does not assign a grade to your diamond.`;
      const track = document.createElement('div');
      track.className = 'bey-grade__track';
      track.setAttribute('aria-hidden', 'true');
      items.forEach((item, index) => {
        const tick = document.createElement('span');
        tick.className = 'bey-grade__tick' + (item === highest[0] ? ' is-standard' : '');
        const marker = document.createElement('span');
        marker.className = 'bey-grade__marker';
        const number = document.createElement('span');
        number.className = 'bey-grade__number';
        number.textContent = index + 1;
        tick.append(marker, number);
        track.append(tick);
        item.classList.toggle('is-standard', item === highest[0]);
        const label = document.createElement('span');
        label.textContent = item.textContent.trim();
        item.replaceChildren(label);
      });
      list.classList.replace('bey-quality-scale', 'bey-grade__labels');
      list.setAttribute('role', 'list');
      list.before(grade);
      grade.append(description, track, list);
    });
  }

  function fitLabels(grade) {
    if (grade.hasAttribute('data-grade-preview') || !grade.isConnected || !grade.getBoundingClientRect().width) return;
    const items = [...grade.querySelectorAll('.bey-grade__labels > li > span')];
    const slot = grade.clientWidth / items.length - 4;
    const fits = items.every(item => {
      const range = document.createRange();
      return [...item.textContent.matchAll(/\S+/g)].every(word => {
        range.setStart(item.firstChild, word.index);
        range.setEnd(item.firstChild, word.index + word[0].length);
        return range.getBoundingClientRect().width <= slot;
      });
    });
    grade.toggleAttribute('data-grade-key', !fits);
  }
  const resize = new ResizeObserver(entries => entries.forEach(({target}) => fitLabels(target)));
  function stop(state) {
    clearTimeout(state.timer);
    state.timer = 0;
    state.active?.removeAttribute('data-glowing');
    state.active = null;
  }
  function eligible(grade) {
    const openComparison = grade.hasAttribute('data-grade-preview') && grade.closest('[data-quality-overview]')?.querySelector('[data-grade-disclosure][open]');
    return visible.has(grade) && !grade.closest('details:not([open])') && !openComparison;
  }
  function run(module, state) {
    if (state.timer || paused || reduced.matches || document.hidden || !module.isConnected) return;
    const options = [...module.querySelectorAll('[data-grade-motion]')].filter(eligible);
    if (!options.length) return;
    const active = options[state.index++ % options.length];
    state.active = active;
    active.setAttribute('data-glowing', '');
    state.timer = setTimeout(() => {
      active.removeAttribute('data-glowing');
      state.active = null;
      // Four seconds of quiet between each eight-second out-and-back pass.
      state.timer = setTimeout(() => { state.timer = 0; run(module, state); }, 4000);
    }, 8000);
  }
  function sync() {
    document.querySelectorAll('[data-grade-pause]').forEach(button => {
      button.hidden = false;
      button.disabled = reduced.matches;
      const label = reduced.matches ? 'Glow off: reduced motion' : paused ? 'Resume glow' : 'Pause glow';
      if (button.textContent !== label) button.textContent = label;
    });
    modules.forEach((state, module) => {
      if (!module.isConnected) { stop(state); modules.delete(module); return; }
      if (paused || reduced.matches || document.hidden || (state.active && !eligible(state.active))) stop(state);
      run(module, state);
    });
  }
  const intersection = new IntersectionObserver(entries => {
    entries.forEach(({target, isIntersecting}) => isIntersecting ? visible.add(target) : visible.delete(target));
    sync();
  }, {threshold: 0.2});

  function discover() {
    frame = 0;
    adaptGuideGradeScales();
    labels.forEach(grade => {
      if (!grade.isConnected) { resize.unobserve(grade); intersection.unobserve(grade); labels.delete(grade); visible.delete(grade); }
    });
    document.querySelectorAll('[data-grade-module]').forEach(module => {
      if (!modules.has(module)) modules.set(module, {index: 0, timer: 0, active: null});
    });
    document.querySelectorAll('.bey-grade').forEach(grade => {
      if (labels.has(grade)) return;
      labels.add(grade); resize.observe(grade); fitLabels(grade);
      if (grade.hasAttribute('data-grade-motion')) intersection.observe(grade);
    });
    sync();
  }
  document.addEventListener('click', event => {
    if (!event.target.closest('[data-grade-pause]') || reduced.matches) return;
    paused = !paused;
    try { localStorage.setItem(storageKey, String(paused)); } catch (_) { /* Keep the in-page preference. */ }
    sync();
  });
  addEventListener('storage', event => { if (event.key === storageKey) { paused = event.newValue === 'true'; sync(); } });
  document.addEventListener('toggle', event => {
    if (!event.target.matches('[data-grade-disclosure], .beylora-c-pdp-buy__scales-fold')) return;
    sync();
    if (event.target.open) requestAnimationFrame(() => { event.target.querySelectorAll('.bey-grade').forEach(fitLabels); sync(); });
  }, true);
  document.addEventListener('visibilitychange', sync);
  reduced.addEventListener('change', sync);
  // PDP section replacement and the theme editor share this idempotent path.
  new MutationObserver(() => { if (!frame) frame = requestAnimationFrame(discover); }).observe(document.body, {childList: true, subtree: true});
  document.fonts?.ready.then(() => labels.forEach(fitLabels));
  addEventListener('pagehide', () => modules.forEach(stop));
  addEventListener('pageshow', sync);
  discover();
})();
